import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { colors } from '@/constants/colors';
import { updateNodeMcuPins, type NodeMcuPins } from '@/api/vendo';

/**
 * Operator-selectable NodeMCU pin map (Step 3 of the NodeMCU setup). Smart
 * dropdowns: each function only offers pins that are valid for it and not already
 * taken, and explains why the rest are disabled. Every change auto-saves
 * (optimistic — no Save button); the chosen GPIOs are baked into the identity
 * sector on the next flash. Standard = the factory wiring (D4/D8/D3); most
 * operators never leave it.
 */

interface PinDef {
   label: string; // NodeMCU silk label
   gpio: number; // ESP8266 GPIO
   interrupt: boolean; // can attachInterrupt (coin pulse needs this)
   spi?: boolean; // part of the HSPI bus (reserved when wired)
   boot?: boolean; // boot-sensitive (idle state matters)
}

// NodeMCU v2/v3 usable digital pins. RX/TX/A0 intentionally omitted.
const PINS: PinDef[] = [
   { label: 'D0', gpio: 16, interrupt: false },
   { label: 'D1', gpio: 5, interrupt: true },
   { label: 'D2', gpio: 4, interrupt: true },
   { label: 'D3', gpio: 0, interrupt: true, boot: true },
   { label: 'D4', gpio: 2, interrupt: true, boot: true },
   { label: 'D5', gpio: 14, interrupt: true, spi: true },
   { label: 'D6', gpio: 12, interrupt: true, spi: true },
   { label: 'D7', gpio: 13, interrupt: true, spi: true },
   { label: 'D8', gpio: 15, interrupt: true, boot: true },
];
const SPI_GPIOS = [14, 12, 13];
const labelFor = (gpio: number | null): string =>
   gpio == null ? '—' : (PINS.find((p) => p.gpio === gpio)?.label ?? `GPIO${gpio}`);

type FuncKey = keyof NodeMcuPins;
// `relay` stays in the saved shape (identity-sector byte 196, always unset) but
// is NOT offered here: the firmware only ever parks that pin LOW, so a
// separate "Relay" row was cosmetic and operators kept asking what to wire to
// it. The coin-power pin IS the relay/transistor that switches the acceptor,
// so the row says so.
const FUNC_LABEL: Record<FuncKey, string> = {
   coinPulse: 'Coin pulse',
   coinPower: 'Coin power (Relay)',
   w5500Cs: 'Ethernet CS',
   relay: 'Relay',
};
const STANDARD: Record<string, number> = { coinPulse: 2, coinPower: 15, w5500Cs: 0 };
const EMPTY: NodeMcuPins = {
   coinPulse: null,
   coinPower: null,
   w5500Cs: null,
   relay: null,
};

type SaveState = 'idle' | 'saving' | 'saved';

interface Props {
   deviceId: string;
   /** true → wired W5500 (SPI reserved, Ethernet CS shown); false → wireless. */
   wired: boolean;
   initialPins?: Partial<NodeMcuPins>;
   onSaved?: (pins: NodeMcuPins) => void;
}

const PinMapEditor = ({ deviceId, wired, initialPins, onSaved }: Props) => {
   const startPins: NodeMcuPins = {
      coinPulse: initialPins?.coinPulse ?? null,
      coinPower: initialPins?.coinPower ?? null,
      w5500Cs: initialPins?.w5500Cs ?? null,
      relay: initialPins?.relay ?? null,
   };
   const hasInitial = Object.values(startPins).some((v) => v != null);
   const [custom, setCustom] = useState(hasInitial);
   const [pins, setPins] = useState<NodeMcuPins>(startPins);
   const [save, setSave] = useState<SaveState>('idle');

   const funcs = useMemo<FuncKey[]>(
      () =>
         (['coinPulse', 'coinPower', wired ? 'w5500Cs' : null].filter(
            Boolean
         ) as FuncKey[]),
      [wired]
   );

   // Optimistic persist: the UI already reflects the change; fire the save in the
   // background. `silent` saves with no status flash — used for tab switches so a
   // plain Standard/Custom click doesn't pop a "Saved ✓".
   const persist = (map: NodeMcuPins, silent = false) => {
      if (!silent) setSave('saving');
      updateNodeMcuPins(deviceId, map)
         .then((res) => {
            if (!silent) {
               setSave('saved');
               window.setTimeout(() => setSave('idle'), 1800);
            }
            onSaved?.(res.pins);
         })
         .catch((err: any) => {
            if (!silent) setSave('idle');
            toast.error(
               err?.response?.data?.error || 'Could not save the pin map'
            );
         });
   };

   const selectMode = (toCustom: boolean) => {
      setCustom(toCustom);
      persist(toCustom ? pins : EMPTY, true); // silent — Standard clears to default
   };

   // Why a pin can't be used for `fn` (null = selectable).
   const reason = (fn: FuncKey, p: PinDef): string | null => {
      if (fn === 'coinPulse' && !p.interrupt) return 'no interrupt';
      if (wired && SPI_GPIOS.includes(p.gpio) && fn !== 'w5500Cs')
         return 'SPI (Ethernet)';
      for (const k of funcs) {
         if (k !== fn && pins[k] === p.gpio) return `used by ${FUNC_LABEL[k]}`;
      }
      return null;
   };

   const setPin = (fn: FuncKey, gpio: number | null) => {
      const next = { ...pins, [fn]: gpio };
      setPins(next);
      persist(next);
   };

   return (
      <Wrap>
         <TopRow>
            <Tabs>
               <Tab $active={!custom} onClick={() => selectMode(false)}>
                  Standard wiring
               </Tab>
               <Tab $active={custom} onClick={() => selectMode(true)}>
                  Custom
               </Tab>
            </Tabs>
            <Status $state={save}>
               {save === 'saving' ? 'Saving…' : save === 'saved' ? 'Saved ✓' : ''}
            </Status>
         </TopRow>

         {!custom ? (
            <>
               {funcs.map((fn) => {
                  const g = STANDARD[fn] ?? null;
                  return (
                     <FuncRow key={fn}>
                        <FuncName>{FUNC_LABEL[fn]}</FuncName>
                        <StaticVal>
                           {g == null ? '— not used' : `${labelFor(g)} (GPIO${g})`}
                        </StaticVal>
                     </FuncRow>
                  );
               })}
               <Hint>Factory carrier-board wiring. Switch to Custom to change.</Hint>
            </>
         ) : (
            <>
               {funcs.map((fn) => (
                  <FuncRow key={fn}>
                     <FuncName>
                        {FUNC_LABEL[fn]}
                        {fn === 'coinPulse' && <Tag title="needs interrupt">⚡</Tag>}
                     </FuncName>
                     <Select
                        value={pins[fn] ?? ''}
                        onChange={(e) =>
                           setPin(
                              fn,
                              e.target.value === '' ? null : Number(e.target.value)
                           )
                        }
                     >
                        <option value="">
                           default ({labelFor(STANDARD[fn] ?? null)})
                        </option>
                        {PINS.map((p) => {
                           const r = reason(fn, p);
                           return (
                              <option key={p.gpio} value={p.gpio} disabled={!!r}>
                                 {p.label} (GPIO{p.gpio})
                                 {p.boot ? ' ⚠boot' : ''}
                                 {r ? ` — ${r}` : ''}
                              </option>
                           );
                        })}
                     </Select>
                  </FuncRow>
               ))}
               <Hint>
                  ⓘ {wired ? 'SPI bus fixed on D5 · D6 · D7. ' : ''}
                  Coin pulse needs an interrupt pin (not D0). ⚠ = boot-sensitive.
                  Changes save automatically.
               </Hint>
            </>
         )}
      </Wrap>
   );
};

export default PinMapEditor;

const Wrap = styled.div`
   display: flex;
   flex-direction: column;
   gap: 12px;
   width: 100%;
   box-sizing: border-box;
   padding: 14px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
`;
const TopRow = styled.div`
   display: flex;
   align-items: center;
   gap: 12px;
`;
// Matches the Rate-panel Coin/Voucher segmented control (SubTabBar/SubTabBtn).
const Tabs = styled.div`
   display: inline-flex;
   gap: 6px;
   background: ${colors.GRAY14};
   padding: 4px;
   border-radius: 8px;
`;
const Tab = styled.button<{ $active: boolean }>`
   background: ${({ $active }) => ($active ? colors.WHITE : 'transparent')};
   color: ${({ $active }) => ($active ? colors.GRAY1 : colors.GRAY5)};
   border: none;
   padding: 6px 14px;
   border-radius: 6px;
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
   box-shadow: ${({ $active }) =>
      $active ? '0 1px 2px rgba(0,0,0,0.06)' : 'none'};
   transition: background 0.15s, color 0.15s;
   &:hover {
      color: ${colors.GRAY1};
   }
`;
const Status = styled.span<{ $state: SaveState }>`
   font-size: 12px;
   font-weight: 600;
   color: ${({ $state }) =>
      $state === 'saved' ? colors.SUCCESS ?? '#16a34a' : colors.GRAY4};
`;
// Read-only pin value in Standard mode — same 240px column as the Custom <Select>
// so the rows stay aligned when toggling between tabs.
const StaticVal = styled.span`
   flex: 0 0 240px;
   width: 240px;
   padding: 6px 8px;
   font-size: 13px;
   font-family: monospace;
   color: ${colors.MAIN_DARK};
`;
const FuncRow = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
`;
const FuncName = styled.span`
   font-size: 13px;
   font-weight: 600;
   color: ${colors.MAIN_DARK};
   display: inline-flex;
   align-items: center;
   gap: 6px;
`;
const Tag = styled.span`
   font-size: 12px;
`;
const Select = styled.select`
   flex: 0 0 240px;
   width: 240px;
   padding: 6px 8px;
   border-radius: 6px;
   border: 1px solid ${colors.GRAY12};
   font-size: 13px;
   background: ${colors.WHITE};
   color: ${colors.MAIN_DARK};
`;
const Hint = styled.div`
   font-size: 11px;
   color: ${colors.GRAY4};
`;
